import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

/**
 * Bloque 4 — Comisiones Partner Points.
 * Fase A: consulta derivada 18% (mine/admin).
 * Fase B: filtros por fecha, detalle ampliado, liquidación mensual (admin),
 * marcar pagada (admin), reporte PDF, i18n ES/EN.
 */
const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const CURRENT_MONTH = new Date().toISOString().slice(0, 7);

const StatusBadge = ({ status, t }) => {
  const map = {
    pending: { cls: "bg-neutral-100 text-neutral-600 border-neutral-300", label: t("comm_status_pending") },
    settled: { cls: "bg-amber-50 text-amber-700 border-amber-400", label: t("comm_status_settled") },
    paid: { cls: "bg-emerald-50 text-emerald-700 border-emerald-500", label: t("comm_status_paid") },
  };
  const s = map[status] || map.pending;
  return (
    <span data-testid={`commission-status-${status}`} className={`font-mono-tech text-[10px] uppercase tracking-wider border px-2 py-0.5 ${s.cls}`}>
      {s.label}
    </span>
  );
};

const MonthCard = ({ m, t, admin, partnerId, onSettle, onMarkPaid, busy }) => {
  const [open, setOpen] = useState(false);
  const sett = m.settlement;
  const pr = sett?.payment_record;
  const canSettle = admin && m.status === "pending" && m.month < CURRENT_MONTH;
  const invBadge = (st) =>
    st === "cancelled" ? <span className="text-red-600 font-mono-tech text-[9px] uppercase border border-red-300 px-1 ml-1">{t("comm_inv_cancelled")}</span>
    : st === "credited" ? <span className="text-amber-700 font-mono-tech text-[9px] uppercase border border-amber-300 px-1 ml-1">{t("comm_inv_credited")}</span>
    : null;
  return (
    <div className="border border-black bg-white">
      <button
        data-testid={`commission-month-${m.month}`}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-neutral-50"
      >
        <span className="font-mono-tech text-sm font-bold">{m.month}</span>
        <StatusBadge status={m.status} t={t} />
        <span className="text-sm text-[var(--text-secondary)] ml-auto">
          {t("comm_col_base")} {money(m.base_total)}
        </span>
        <span className="font-display font-black">{money(m.commission_total)}</span>
      </button>
      {open && (
        <div className="border-t border-black px-4 py-2 space-y-1">
          <div className="grid grid-cols-12 gap-2 text-[10px] font-mono-tech uppercase tracking-wider text-[var(--text-secondary)] py-1">
            <span className="col-span-2">{t("comm_col_invoice")}</span>
            <span className="col-span-3">{t("comm_col_client")}</span>
            <span className="col-span-2">{t("comm_col_issued")}</span>
            <span className="col-span-2">{t("comm_col_paid")}</span>
            <span className="col-span-1 text-right">{t("comm_col_base")}</span>
            <span className="col-span-2 text-right">{t("comm_col_commission")}</span>
          </div>
          {m.invoices.map((i) => (
            <div key={i.invoice_number} data-testid={`commission-row-${i.invoice_number}`}
              className="py-1 border-b border-dashed border-neutral-200 last:border-0">
              <div className="grid grid-cols-12 gap-2 text-xs">
                <span className="col-span-2 font-mono-tech font-bold">{i.invoice_number}{invBadge(i.invoice_status)}</span>
                <span className="col-span-3 truncate">{i.company}</span>
                <span className="col-span-2">{i.issue_date}</span>
                <span className="col-span-2">{i.paid_date}</span>
                <span className="col-span-1 text-right">
                  {money(i.base)}
                  {i.credits_pretax > 0 && <span className="text-red-600 block">−{money(i.credits_pretax)} NC</span>}
                </span>
                <span className="col-span-2 text-right font-bold">{money(i.commission)} <span className="font-normal text-[var(--text-secondary)]">({Number(i.rate).toFixed(0)}%)</span></span>
              </div>
              {(i.credit_notes || []).map((cn) => (
                <div key={cn.number} className="text-[11px] text-red-700 pl-2">
                  ↳ {cn.number} · {cn.date} · −{money(cn.amount)}{cn.reason ? ` · ${cn.reason}` : ""}
                </div>
              ))}
            </div>
          ))}
          {sett && (sett.adjustments || []).map((a, idx) => (
            <div key={idx} data-testid={`commission-adjustment-${a.invoice_number || a.source_month}`}
              className="grid grid-cols-12 gap-2 text-xs py-1 text-amber-700">
              <span className="col-span-10 italic">
                {t("comm_adj_from")} {a.source_month}
                {a.invoice_number ? ` · ${a.invoice_number}` : ""}
                {a.cn_numbers ? ` · ${a.cn_numbers}` : ""}
                {a.cn_date ? ` · ${a.cn_date}` : ""}
                {a.reason ? ` · ${a.reason}` : ""}
              </span>
              <span className="col-span-2 text-right font-bold">{money(a.amount)}</span>
            </div>
          ))}
          {sett && (
            <div className="flex items-center justify-between text-xs pt-2 border-t border-neutral-200">
              <span className="text-[var(--text-secondary)]">
                {t("comm_status_settled")}: {(sett.settled_at || "").slice(0, 10)}
                {pr && (
                  <> · {t("comm_paid_via")} {pr.method === "Cheque" ? `${t("comm_method_check")} #${pr.check_number}` : `Zelle${pr.zelle_reference ? ` (${pr.zelle_reference})` : ""}`} · {pr.paid_date} · {money(pr.amount_paid)} · {t("comm_registered_by")} {pr.recorded_by_name}</>
                )}
              </span>
              <span className="font-bold">{t("comm_net")} {money(sett.net_total)}</span>
            </div>
          )}
          {(canSettle || (admin && sett && sett.status === "settled")) && (
            <div className="flex justify-end gap-2 pt-2">
              {canSettle && (
                <button
                  data-testid={`settle-btn-${m.month}`}
                  disabled={busy}
                  onClick={() => onSettle(partnerId, m.month)}
                  className="border border-black bg-black text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-neutral-800 disabled:opacity-50"
                >
                  {t("comm_settle")}
                </button>
              )}
              {admin && sett && sett.status === "settled" && (
                <button
                  data-testid={`mark-paid-btn-${m.month}`}
                  disabled={busy}
                  onClick={() => onMarkPaid({ ...sett, month: m.month })}
                  className="border border-emerald-600 text-emerald-700 px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-emerald-50 disabled:opacity-50"
                >
                  {t("comm_mark_paid")}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PartnerBlock = ({ p, showName, t, admin, filters, onSettle, onMarkPaid, busy }) => {
  const downloadPdf = async () => {
    try {
      const params = { ...filters };
      if (admin) params.partner_id = p.partner_id;
      const res = await axios.get(`${API}/partner-commissions/report-pdf`, {
        params, responseType: "blob", withCredentials: true,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error(t("comm_pdf_error"));
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          {showName && (
            <div className="font-display text-lg font-bold">
              {p.business_name || p.partner_name}
              <span className="font-normal text-sm text-[var(--text-secondary)]"> · {p.partner_name}</span>
            </div>
          )}
          <div className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-[var(--text-secondary)]">
            {t("comm_rate_line")} {Number(p.commission_rate).toFixed(2)}% · {t("comm_rate_suffix")}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button
            data-testid={`commission-pdf-btn${admin ? `-${p.partner_id}` : ""}`}
            onClick={downloadPdf}
            className="border border-black px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-colors"
          >
            {t("comm_pdf")}
          </button>
          <div className="text-right">
            <div className="font-mono-tech text-[10px] uppercase text-[var(--text-secondary)]">
              {filters.from || filters.to ? t("comm_period_total") : t("comm_total")}
            </div>
            <div data-testid="commission-total" className="font-display text-2xl font-black">
              {money(p.total_commission)}
            </div>
          </div>
        </div>
      </div>
      {(p.months || []).length === 0 ? (
        <div className="border border-dashed border-neutral-300 p-6 text-center text-sm text-[var(--text-secondary)]">
          {t("comm_empty")}
        </div>
      ) : (
        p.months.map((m) => (
          <MonthCard key={m.month} m={m} t={t} admin={admin} partnerId={p.partner_id}
            onSettle={onSettle} onMarkPaid={onMarkPaid} busy={busy} />
        ))
      )}
    </div>
  );
};

const PayModal = ({ sett, t, busy, onConfirm, onClose }) => {
  const [form, setForm] = useState({
    paid_date: new Date().toISOString().slice(0, 10),
    method: "Zelle",
    check_number: "",
    zelle_reference: "",
    amount_paid: sett.net_total,
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const invalid = !form.paid_date || (form.method === "Cheque" && !form.check_number.trim());
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="pay-modal">
      <div className="bg-white border-2 border-black w-full max-w-md p-6 space-y-4">
        <div>
          <div className="label-overline">{sett.month}</div>
          <h3 className="font-display text-xl font-black">{t("comm_pay_title")}</h3>
          <p className="text-xs text-[var(--text-secondary)]">{t("comm_pay_note")}</p>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <label className="font-mono-tech text-[10px] uppercase block">{t("comm_pay_date")} *</label>
            <input data-testid="pay-date" type="date" value={form.paid_date} onChange={set("paid_date")}
              className="border border-black px-2 py-1 w-full" />
          </div>
          <div>
            <label className="font-mono-tech text-[10px] uppercase block">{t("comm_pay_method")} *</label>
            <select data-testid="pay-method" value={form.method} onChange={set("method")}
              className="border border-black px-2 py-1 w-full bg-white">
              <option value="Zelle">Zelle</option>
              <option value="Cheque">{t("comm_method_check")}</option>
            </select>
          </div>
          {form.method === "Cheque" ? (
            <div className="col-span-2">
              <label className="font-mono-tech text-[10px] uppercase block">{t("comm_check_number")} *</label>
              <input data-testid="pay-check-number" value={form.check_number} onChange={set("check_number")}
                className="border border-black px-2 py-1 w-full" />
            </div>
          ) : (
            <div className="col-span-2">
              <label className="font-mono-tech text-[10px] uppercase block">{t("comm_zelle_ref")}</label>
              <input data-testid="pay-zelle-ref" value={form.zelle_reference} onChange={set("zelle_reference")}
                className="border border-black px-2 py-1 w-full" />
            </div>
          )}
          <div>
            <label className="font-mono-tech text-[10px] uppercase block">{t("comm_pay_amount")}</label>
            <input data-testid="pay-amount" type="number" step="0.01" value={form.amount_paid} onChange={set("amount_paid")}
              className="border border-black px-2 py-1 w-full" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button data-testid="pay-cancel" onClick={onClose}
            className="border border-neutral-400 px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-neutral-100">
            {t("comm_cancel")}
          </button>
          <button data-testid="pay-confirm" disabled={invalid || busy}
            onClick={() => onConfirm(sett.id, form)}
            className="border border-emerald-600 bg-emerald-600 text-white px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50">
            {t("comm_pay_confirm")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function CommissionsPanel({ admin = false }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [applied, setApplied] = useState({});
  const [partnerSel, setPartnerSel] = useState("");
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [payFor, setPayFor] = useState(null);

  const load = useCallback((f, pid) => {
    setLoading(true);
    const url = `${API}/partner-commissions/${admin ? "admin" : "mine"}`;
    const params = { ...f, ...(admin && pid ? { partner_id: pid } : {}) };
    axios
      .get(url, { params, withCredentials: true })
      .then(({ data }) => {
        setData(data);
        setError("");
        if (admin && !pid && data?.partners) {
          setPartnerOptions(data.partners.map((p) => ({
            id: p.partner_id, label: p.business_name || p.partner_name,
          })));
        }
      })
      .catch(() => setError(t("comm_error")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  useEffect(() => { load(applied, partnerSel); }, [load, applied, partnerSel]);

  const onSettle = async (partnerId, month) => {
    setBusy(true);
    try {
      await axios.post(`${API}/partner-commissions/settle`,
        { partner_id: partnerId, month }, { withCredentials: true });
      toast.success(t("comm_settled_ok"));
      load(applied, partnerSel);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("comm_settle_error"));
    } finally { setBusy(false); }
  };

  const onConfirmPay = async (sid, form) => {
    setBusy(true);
    try {
      await axios.post(`${API}/partner-commissions/settlements/${sid}/mark-paid`,
        { ...form, amount_paid: Number(form.amount_paid) }, { withCredentials: true });
      toast.success(t("comm_paid_ok"));
      setPayFor(null);
      load(applied, partnerSel);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("comm_settle_error"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="commissions-panel" className="space-y-6">
      {payFor && (
        <PayModal sett={payFor} t={t} busy={busy} onConfirm={onConfirmPay} onClose={() => setPayFor(null)} />
      )}
      <div>
        <div className="label-overline">PARTNER POINTS</div>
        <h2 className="font-display text-3xl font-black tracking-tight">
          {admin ? t("comm_title_admin") : t("comm_title_mine")}
        </h2>
        <p className="text-sm text-[var(--text-secondary)] mt-1">{t("comm_subtitle")}</p>
      </div>

      <div className="flex items-end gap-3 flex-wrap border border-neutral-200 bg-neutral-50 px-4 py-3">
        {admin && (
          <div>
            <label className="font-mono-tech text-[10px] uppercase text-[var(--text-secondary)] block">{t("comm_select_partner")}</label>
            <select data-testid="commission-partner-select" value={partnerSel}
              onChange={(e) => setPartnerSel(e.target.value)}
              className="border border-black px-2 py-1 text-sm bg-white min-w-[180px]">
              <option value="">{t("comm_all_partners")}</option>
              {partnerOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="font-mono-tech text-[10px] uppercase text-[var(--text-secondary)] block">{t("comm_from")}</label>
          <input data-testid="commission-filter-from" type="date" value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="border border-black px-2 py-1 text-sm bg-white" />
        </div>
        <div>
          <label className="font-mono-tech text-[10px] uppercase text-[var(--text-secondary)] block">{t("comm_to")}</label>
          <input data-testid="commission-filter-to" type="date" value={to}
            onChange={(e) => setTo(e.target.value)}
            className="border border-black px-2 py-1 text-sm bg-white" />
        </div>
        <button data-testid="commission-filter-apply"
          onClick={() => setApplied({ ...(from ? { from } : {}), ...(to ? { to } : {}) })}
          className="border border-black bg-black text-white px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-neutral-800">
          {t("comm_filter")}
        </button>
        {(applied.from || applied.to || partnerSel) && (
          <button data-testid="commission-filter-clear"
            onClick={() => { setFrom(""); setTo(""); setApplied({}); setPartnerSel(""); }}
            className="border border-neutral-400 px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-neutral-100">
            {t("comm_clear")}
          </button>
        )}
      </div>

      {loading && <div className="font-mono-tech text-sm">LOADING…</div>}
      {error && <div className="text-sm text-red-600">{error}</div>}
      {!loading && !error && !admin && data && (
        <PartnerBlock p={data} showName={false} t={t} admin={false} filters={applied}
          onSettle={onSettle} onMarkPaid={setPayFor} busy={busy} />
      )}
      {!loading && !error && admin && (
        <div className="space-y-8">
          {(data?.partners || []).length === 0 && (
            <div className="text-sm text-[var(--text-secondary)]">{t("comm_no_partners")}</div>
          )}
          {(data?.partners || []).map((p) => (
            <PartnerBlock key={p.partner_id} p={p} showName t={t} admin filters={applied}
              onSettle={onSettle} onMarkPaid={setPayFor} busy={busy} />
          ))}
        </div>
      )}
    </div>
  );
}
